import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import TargetWidget from '../components/TargetWidget';

const KERALA_DISTRICTS = [
  'Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam',
  'Idukki','Ernakulam','Thrissur','Palakkad','Malappuram',
  'Kozhikode','Wayanad','Kannur','Kasaragod'
];
const LEADS_THROUGH_OPTIONS = [
  'Consultancy','Institution','Reference','Website',
  'OLX','Instagram','Facebook','LinkedIn','Others'
];

const DAILY_LEAD_TARGET       = 100;
const DAILY_CONVERSION_TARGET = 10;
const CONVERSION_REWARD       = 50;

const getTodayDate = () => new Date().toISOString().slice(0, 10);
const getToken     = () => localStorage.getItem('token');

const EMPTY_MEMBER_FORM = {
  date_of_lead: getTodayDate(), name: '', place: '', district: '',
  contact_number: '', whatsapp_number: '', email_id: '', leads_through: '', resume_file: null,
};
const EMPTY_COMPANY_FORM = {
  date_of_lead: getTodayDate(), company_name: '', place: '', district: '',
  contact_number: '', whatsapp_number: '', email_id: '', leads_through: '', notes: '',
};

const SOURCE_COLORS = {
  Instagram:   'bg-pink-500/15 text-pink-700 border-pink-300',
  Facebook:    'bg-blue-500/15 text-blue-700 border-blue-300',
  LinkedIn:    'bg-sky-500/15 text-sky-800 border-sky-300',
  Website:     'bg-indigo-500/15 text-indigo-700 border-indigo-300',
  OLX:         'bg-amber-500/15 text-amber-700 border-amber-300',
  Reference:   'bg-emerald-500/15 text-emerald-700 border-emerald-300',
  Consultancy: 'bg-purple-500/15 text-purple-700 border-purple-300',
  Institution: 'bg-teal-500/15 text-teal-700 border-teal-300',
  Others:      'bg-gray-500/15 text-gray-600 border-gray-300',
};

const CALL_STATUS_META = {
  Rejected:    { cls: 'bg-rose-100 text-rose-700 border-rose-300',          dot: 'bg-rose-500'    },
  'No Hope':   { cls: 'bg-orange-100 text-orange-700 border-orange-300',    dot: 'bg-orange-400'  },
  Progressing: { cls: 'bg-blue-100 text-blue-700 border-blue-300',          dot: 'bg-blue-500'    },
  Converted:   { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500' },
};

const SourceBadge = ({ src }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-extrabold tracking-wide ${SOURCE_COLORS[src] || SOURCE_COLORS.Others}`}>
    {src}
  </span>
);

const CallStatusBadge = ({ status }) => {
  if (!status) return <span className="text-[11px] font-semibold text-gray-300">— Pending —</span>;
  const m = CALL_STATUS_META[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-extrabold ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {status}
    </span>
  );
};

const TargetBar = ({ label, value, target, color }) => {
  const pct = Math.min(100, Math.round((value / target) * 100));
  const over = value >= target;
  return (
    <div className="flex-1 min-w-[160px]">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">{label}</p>
        <p className={`text-xs font-black ${over ? 'text-emerald-600' : 'text-gray-700'}`}>{value}<span className="font-normal text-gray-400">/{target}</span></p>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${over ? 'bg-emerald-500' : color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className={`mt-0.5 text-[10px] font-semibold ${over ? 'text-emerald-600' : 'text-gray-400'}`}>{pct}% {over ? '✓ Target met!' : 'of daily target'}</p>
    </div>
  );
};

const inputCls  = 'w-full rounded-2xl border border-gray-200 bg-white/80 px-3.5 py-2.5 text-sm font-medium text-gray-800 shadow-sm outline-none transition focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 placeholder:text-gray-400';
const selectCls = `${inputCls} appearance-none`;

const Lbl = ({ children, req }) => (
  <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
    {children}{req && <span className="ml-0.5 text-rose-500">*</span>}
  </p>
);

// ─────── Member Leads Tab ────────────────────────────────────────────────────
const MemberLeadsTab = ({ API_URL, onStatsChange }) => {
  const [leads, setLeads]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editId, setEditId]             = useState(null);
  const [form, setForm]                 = useState({ ...EMPTY_MEMBER_FORM });
  const [saving, setSaving]             = useState(false);
  const [feedback, setFeedback]         = useState('');
  const [delId, setDelId]               = useState(null);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [filterOpen, setFilterOpen]         = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');
  const [filterSource, setFilterSource]     = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterForwarded, setFilterForwarded] = useState('');
  const [filterCallStatus, setFilterCallStatus] = useState('');
  const [page, setPage]                     = useState(1);
  // Telecaller forward state
  const [telecallers, setTelecallers]   = useState([]);
  const [forwardRowId, setForwardRowId] = useState(null);
  const [selectedTC, setSelectedTC]     = useState('');
  const [forwarding, setForwarding]     = useState(false);
  const fileRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/agent/member-leads`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = r.data || [];
      setLeads(data);
      if (onStatsChange) onStatsChange(data);
    } catch { setLeads([]); } finally { setLoading(false); }
  }, [API_URL, onStatsChange]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    axios.get(`${API_URL}/api/agent/telecallers`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => setTelecallers(r.data || []))
      .catch(() => {});
  }, [API_URL]);

  const openNew  = () => { setEditId(null); setForm({ ...EMPTY_MEMBER_FORM, date_of_lead: getTodayDate() }); setFeedback(''); setShowForm(true); setForwardRowId(null); };
  const openEdit = (l) => {
    setEditId(l.id);
    setForm({ date_of_lead: l.date_of_lead||getTodayDate(), name: l.name||'', place: l.place||'', district: l.district||'',
              contact_number: l.contact_number||'', whatsapp_number: l.whatsapp_number||'', email_id: l.email_id||'',
              leads_through: l.leads_through||'', resume_file: null });
    setFeedback(''); setShowForm(true); setForwardRowId(null);
  };
  const onChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume_file') setForm(p => ({ ...p, resume_file: files[0]||null }));
    else setForm(p => ({ ...p, [name]: value }));
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setFeedback('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => { if (v!==null && v!==undefined && v!=='') fd.append(k,v); });
      let updated;
      if (editId) {
        const r = await axios.put(`${API_URL}/api/agent/member-leads/${editId}`, fd, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } });
        updated = leads.map(l => l.id===editId ? r.data.lead : l);
      } else {
        const r = await axios.post(`${API_URL}/api/agent/member-leads`, fd, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } });
        updated = [r.data.lead, ...leads];
      }
      setLeads(updated);
      if (onStatsChange) onStatsChange(updated);
      setShowForm(false); setEditId(null); setForm({ ...EMPTY_MEMBER_FORM, date_of_lead: getTodayDate() });
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) { setFeedback(err?.response?.data?.error || 'Failed to save lead.'); } finally { setSaving(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete this member lead?')) return;
    setDelId(id);
    try {
      await axios.delete(`${API_URL}/api/agent/member-leads/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const updated = leads.filter(l => l.id!==id);
      setLeads(updated);
      if (onStatsChange) onStatsChange(updated);
    } catch { alert('Failed to delete.'); } finally { setDelId(null); }
  };

  const openForward  = (id) => { setForwardRowId(id); setSelectedTC(''); setShowForm(false); };
  const closeForward = () => { setForwardRowId(null); setSelectedTC(''); };
  const doForward    = async (leadId) => {
    if (!selectedTC) return;
    const tc = telecallers.find(t => t.user_id === selectedTC);
    if (!tc) return;
    setForwarding(true);
    try {
      const r = await axios.put(
        `${API_URL}/api/agent/member-leads/${leadId}/forward-telecaller`,
        { telecaller_id: tc.user_id, telecaller_name: tc.name },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const updated = leads.map(l => l.id===leadId ? r.data.lead : l);
      setLeads(updated);
      if (onStatsChange) onStatsChange(updated);
      closeForward();
    } catch { alert('Failed to forward.'); } finally { setForwarding(false); }
  };

  const today = getTodayDate();
  const todayLeads       = leads.filter(l => l.date_of_lead === today).length;
  const todayConversions = leads.filter(l => l.telecaller_status === 'Converted' && l.updated_at?.slice(0,10) === today).length;

  const STATUS_FILTERS = ['All','Pending','Progressing','Converted','No Hope','Rejected'];
  const statusCount = {
    All: leads.length,
    Pending: leads.filter(l => !l.telecaller_status).length,
    Progressing: leads.filter(l => l.telecaller_status==='Progressing').length,
    Converted: leads.filter(l => l.telecaller_status==='Converted').length,
    'No Hope': leads.filter(l => l.telecaller_status==='No Hope').length,
    Rejected: leads.filter(l => l.telecaller_status==='Rejected').length,
  };

  const activeFilterCount = [filterDateFrom, filterDateTo, filterSource, filterDistrict, filterForwarded, filterCallStatus].filter(Boolean).length;

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const matchSearch      = !q || l.name?.toLowerCase().includes(q) || l.contact_number?.includes(q) || l.district?.toLowerCase().includes(q) || l.leads_through?.toLowerCase().includes(q) || l.telecaller_name?.toLowerCase().includes(q);
    const matchStatus      = statusFilter === 'All' || (statusFilter === 'Pending' ? !l.telecaller_status : l.telecaller_status === statusFilter);
    const matchDateFrom    = !filterDateFrom    || l.date_of_lead >= filterDateFrom;
    const matchDateTo      = !filterDateTo      || l.date_of_lead <= filterDateTo;
    const matchSource      = !filterSource      || l.leads_through === filterSource;
    const matchDistrict    = !filterDistrict    || l.district === filterDistrict;
    const matchForwarded   = !filterForwarded   || (filterForwarded === 'yes' ? !!l.telecaller_name : !l.telecaller_name);
    const matchCallStatus  = !filterCallStatus  || (filterCallStatus === 'Pending' ? !l.telecaller_status : l.telecaller_status === filterCallStatus);
    return matchSearch && matchStatus && matchDateFrom && matchDateTo && matchSource && matchDistrict && matchForwarded && matchCallStatus;
  });

  const PAGE_SIZE  = 10;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div>
      {/* Search + Filter + Add button */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <input type="text" placeholder="Search name, contact, district, telecaller…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 pl-10 text-sm shadow-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100" />
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
        </div>
        <button type="button" onClick={() => setFilterOpen(p => !p)}
          className={`relative shrink-0 rounded-[20px] border px-5 py-3 text-sm font-extrabold transition-all ${
            filterOpen || activeFilterCount > 0
              ? 'border-yellow-400 bg-yellow-50 text-yellow-700 shadow-[0_4px_16px_rgba(250,204,21,0.25)]'
              : 'border-gray-200 bg-white text-gray-600 hover:border-yellow-400 hover:bg-yellow-50'
          }`}>
          ⚙ Filters
          {activeFilterCount > 0 && (
            <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-black text-[9px] font-black text-yellow-300">{activeFilterCount}</span>
          )}
        </button>
        <button type="button" onClick={openNew}
          className="shrink-0 rounded-[20px] border border-black bg-black px-5 py-3 text-sm font-extrabold text-yellow-300 shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:bg-[#222] hover:shadow-[0_12px_32px_rgba(0,0,0,0.35)] active:scale-95 transition-all">
          + Add Member Lead
        </button>
      </div>

      {/* Advanced Filter Panel */}
      {filterOpen && (
        <div className="mb-4 overflow-hidden rounded-[22px] border border-yellow-200 bg-[linear-gradient(135deg,#fefce8,#fef9c3/60%)] p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-[0.26em] text-gray-500">⚙ Advanced Filters</p>
            {activeFilterCount > 0 && (
              <button type="button"
                onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); setFilterSource(''); setFilterDistrict(''); setFilterForwarded(''); setFilterCallStatus(''); setPage(1); }}
                className="rounded-xl border border-rose-200 bg-white px-3 py-1 text-[10px] font-black text-rose-500 hover:bg-rose-50 transition">
                ✕ Clear All Filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <div>
              <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">Date From</p>
              <input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100" />
            </div>
            <div>
              <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">Date To</p>
              <input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1); }}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100" />
            </div>
            <div>
              <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">Source</p>
              <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-yellow-400 appearance-none">
                <option value="">All Sources</option>
                {LEADS_THROUGH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">District</p>
              <select value={filterDistrict} onChange={e => { setFilterDistrict(e.target.value); setPage(1); }}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-yellow-400 appearance-none">
                <option value="">All Districts</option>
                {KERALA_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">Forwarded</p>
              <select value={filterForwarded} onChange={e => { setFilterForwarded(e.target.value); setPage(1); }}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-yellow-400 appearance-none">
                <option value="">All</option>
                <option value="yes">Forwarded</option>
                <option value="no">Not Forwarded</option>
              </select>
            </div>
            <div>
              <p className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">Call Status</p>
              <select value={filterCallStatus} onChange={e => { setFilterCallStatus(e.target.value); setPage(1); }}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-yellow-400 appearance-none">
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Progressing">Progressing</option>
                <option value="Converted">Converted</option>
                <option value="No Hope">No Hope</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {filterDateFrom  && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-[10px] font-extrabold text-yellow-800">📅 From: {filterDateFrom} <button onClick={() => { setFilterDateFrom('');  setPage(1); }} className="ml-0.5 hover:text-yellow-900">✕</button></span>}
              {filterDateTo    && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-[10px] font-extrabold text-yellow-800">📅 To: {filterDateTo} <button onClick={() => { setFilterDateTo('');    setPage(1); }} className="ml-0.5 hover:text-yellow-900">✕</button></span>}
              {filterSource    && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-[10px] font-extrabold text-yellow-800">🔗 {filterSource} <button onClick={() => { setFilterSource('');    setPage(1); }} className="ml-0.5 hover:text-yellow-900">✕</button></span>}
              {filterDistrict  && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-[10px] font-extrabold text-yellow-800">📍 {filterDistrict} <button onClick={() => { setFilterDistrict('');  setPage(1); }} className="ml-0.5 hover:text-yellow-900">✕</button></span>}
              {filterForwarded   && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-[10px] font-extrabold text-yellow-800">📞 {filterForwarded === 'yes' ? 'Forwarded' : 'Not Forwarded'} <button onClick={() => { setFilterForwarded(''); setPage(1); }} className="ml-0.5 hover:text-yellow-900">✕</button></span>}
              {filterCallStatus  && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-[10px] font-extrabold text-yellow-800">🟡 Call: {filterCallStatus} <button onClick={() => { setFilterCallStatus(''); setPage(1); }} className="ml-0.5 hover:text-yellow-900">✕</button></span>}
            </div>
          )}
        </div>
      )}

      {/* Status filter pills */}
      <div className="mb-5 flex flex-wrap gap-2">
        {STATUS_FILTERS.map(f => (
          <button key={f} type="button" onClick={() => { setStatusFilter(f); setPage(1); }}
            className={`rounded-[18px] border px-3 py-1.5 text-[11px] font-extrabold transition ${
              statusFilter === f
                ? 'border-black bg-black text-yellow-300 shadow-[0_4px_12px_rgba(0,0,0,0.18)]'
                : 'border-gray-200 bg-white text-gray-600 hover:border-yellow-400'
            }`}>
            {f} <span className="opacity-60">({statusCount[f] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="mb-7 overflow-hidden rounded-[28px] border border-gray-200 bg-gray-50 shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
          <div className="border-b border-black/10 bg-[linear-gradient(135deg,#0a0a0a,#1f1f1f)] px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.26em] text-yellow-400">{editId ? 'Editing Lead' : 'New Member Lead'}</p>
                <p className="mt-0.5 text-lg font-black text-white">{editId ? 'Update member lead details' : 'Add a new member lead'}</p>
              </div>
              <button type="button" onClick={() => setShowForm(false)}
                className="rounded-2xl border border-white/20 px-4 py-1.5 text-xs font-extrabold text-white/80 hover:bg-white/10 transition">✕ Cancel</button>
            </div>
          </div>
          <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3">
            <div><Lbl req>Date of Lead</Lbl><input type="date" name="date_of_lead" value={form.date_of_lead} onChange={onChange} required className={inputCls} /></div>
            <div><Lbl req>Name</Lbl><input type="text" name="name" value={form.name} onChange={onChange} required placeholder="Full name" className={inputCls} /></div>
            <div><Lbl>Place</Lbl><input type="text" name="place" value={form.place} onChange={onChange} placeholder="Place (optional)" className={inputCls} /></div>
            <div><Lbl>District</Lbl>
              <select name="district" value={form.district} onChange={onChange} className={selectCls}>
                <option value="">Select district</option>
                {KERALA_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><Lbl req>Contact Number</Lbl><input type="tel" name="contact_number" value={form.contact_number} onChange={onChange} required placeholder="10-13 digit number" maxLength={13} className={inputCls} /></div>
            <div><Lbl>WhatsApp Number</Lbl><input type="tel" name="whatsapp_number" value={form.whatsapp_number} onChange={onChange} placeholder="10-13 digit (optional)" maxLength={13} className={inputCls} /></div>
            <div><Lbl>Email ID</Lbl><input type="email" name="email_id" value={form.email_id} onChange={onChange} placeholder="email@example.com" className={inputCls} /></div>
            <div><Lbl req>Leads Through</Lbl>
              <select name="leads_through" value={form.leads_through} onChange={onChange} required className={selectCls}>
                <option value="">Select source</option>
                {LEADS_THROUGH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div><Lbl>Resume (PDF or JPEG)</Lbl>
              <input ref={fileRef} type="file" name="resume_file" accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg,image/jpg" onChange={onChange}
                className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 file:mr-3 file:rounded-xl file:border-0 file:bg-yellow-100 file:px-3 file:py-1 file:text-xs file:font-extrabold file:text-yellow-700 hover:file:bg-yellow-200" />
            </div>
            <div className="flex items-end sm:col-span-2 lg:col-span-3 gap-3">
              {feedback && <p className="text-xs font-bold text-rose-600">{feedback}</p>}
              <button type="submit" disabled={saving}
                className="ml-auto rounded-[18px] border border-black bg-black px-7 py-2.5 text-sm font-extrabold text-yellow-300 shadow-[0_6px_20px_rgba(0,0,0,0.25)] hover:bg-[#222] hover:shadow-[0_10px_28px_rgba(0,0,0,0.35)] disabled:opacity-50 transition-all">
                {saving ? 'Saving…' : editId ? 'Update Lead' : 'Save Lead'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-sm font-semibold text-gray-400">
          <span className="mr-2 animate-spin text-xl">⟳</span> Loading leads…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-[24px] border border-dashed border-gray-300 bg-gray-50/60 py-20 text-center">
          <p className="text-4xl">👤</p>
          <p className="mt-3 text-base font-black text-gray-500">{search||statusFilter!=='All' ? 'No leads match your filters.' : 'No member leads yet.'}</p>
          <p className="mt-1 text-sm text-gray-400">Click "+ Add Member Lead" to capture your first lead.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[24px] border border-gray-200 bg-white shadow-[0_8px_32px_rgba(15,23,42,0.06)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#111] text-left">
                {['Date','Name','District','Contact','Source','Telecaller','Call Status','Resume','Actions'].map(h => (
                  <th key={h} className="whitespace-nowrap px-4 py-3.5 text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((l, i) => (
                <React.Fragment key={l.id}>
                  <tr className={`border-b transition-colors hover:bg-yellow-50/50 ${i%2===0?'bg-white':'bg-gray-50/60'}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-400">{l.date_of_lead||'-'}</td>
                    <td className="px-4 py-3 font-extrabold text-gray-900">{l.name}</td>
                    <td className="px-4 py-3 text-gray-500">{l.district||'-'}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <a href={`tel:${l.contact_number}`} className="font-bold text-gray-900 hover:underline">{l.contact_number}</a>
                      {l.whatsapp_number && (
                        <a href={`https://wa.me/91${l.whatsapp_number}`} target="_blank" rel="noreferrer"
                          className="ml-2 text-emerald-600 text-xs hover:underline">WA</a>
                      )}
                    </td>
                    <td className="px-4 py-3"><SourceBadge src={l.leads_through} /></td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {l.telecaller_name
                        ? <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-700"><span className="text-base">📞</span>{l.telecaller_name}</span>
                        : <span className="text-xs text-gray-300">— Not forwarded —</span>}
                    </td>
                    <td className="px-4 py-3"><CallStatusBadge status={l.telecaller_status} /></td>
                    <td className="px-4 py-3">
                      {l.resume_file
                        ? <a href={`${API_URL}/${l.resume_file}`} target="_blank" rel="noreferrer" className="rounded-xl border border-gray-300 bg-gray-100 px-2.5 py-1 text-[11px] font-extrabold text-gray-700 hover:bg-yellow-100 hover:border-yellow-400">View ↗</a>
                        : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <button type="button" onClick={() => !l.telecaller_name && openEdit(l)}
                          disabled={!!l.telecaller_name}
                          title={l.telecaller_name ? 'Cannot edit — lead already forwarded to telecaller' : 'Edit lead'}
                          className={`rounded-xl border px-3 py-1.5 text-[11px] font-extrabold transition ${
                            l.telecaller_name
                              ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:bg-yellow-50'
                          }`}>Edit</button>
                        {l.telecaller_status !== 'Converted' && (
                          <button type="button"
                            onClick={() => forwardRowId === l.id ? closeForward() : openForward(l.id)}
                            className={`rounded-xl border px-3 py-1.5 text-[11px] font-extrabold transition ${
                              forwardRowId === l.id
                                ? 'border-black bg-black text-yellow-300'
                                : l.telecaller_name
                                  ? 'border-gray-300 bg-white text-gray-500 hover:border-yellow-400 hover:bg-yellow-50'
                                  : 'border-black bg-black text-yellow-300 hover:bg-[#222]'
                            }`}>
                            {forwardRowId === l.id ? '✕ Cancel' : l.telecaller_name ? '↪ Reassign' : '→ Forward'}
                          </button>
                        )}
                        <button type="button" onClick={() => !l.telecaller_name && onDelete(l.id)}
                          disabled={delId===l.id || !!l.telecaller_name}
                          title={l.telecaller_name ? 'Cannot delete — lead already forwarded to telecaller' : 'Delete lead'}
                          className={`rounded-xl border px-3 py-1.5 text-[11px] font-extrabold transition ${
                            l.telecaller_name
                              ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
                              : 'border-rose-200 bg-white text-rose-500 hover:border-rose-400 hover:bg-rose-50'
                          } disabled:opacity-50`}>
                          {delId===l.id?'…':'Del'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {/* Forward row */}
                  {forwardRowId === l.id && (
                    <tr className="border-b bg-yellow-50">
                      <td colSpan={9} className="px-5 py-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-xs font-black uppercase tracking-widest text-gray-500">→ Forward to Telecaller:</span>
                          {telecallers.length === 0 ? (
                            <span className="text-xs text-gray-400">No telecallers found. Add a Telecaller staff member first.</span>
                          ) : (
                            <>
                              <select value={selectedTC} onChange={e => setSelectedTC(e.target.value)}
                                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-yellow-400">
                                <option value="">— Select telecaller —</option>
                                {telecallers.map(t => (
                                  <option key={t.user_id} value={t.user_id}>{t.name}</option>
                                ))}
                              </select>
                              <button type="button" onClick={() => doForward(l.id)} disabled={!selectedTC || forwarding}
                                className="rounded-xl border border-black bg-black px-4 py-2 text-xs font-extrabold text-yellow-300 hover:bg-[#222] disabled:opacity-50 transition">
                                {forwarding ? 'Forwarding…' : 'Confirm Forward'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filtered.length > PAGE_SIZE && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] font-semibold text-gray-400">
            Showing <span className="font-black text-gray-700">{(safePage-1)*PAGE_SIZE+1}–{Math.min(safePage*PAGE_SIZE, filtered.length)}</span> of <span className="font-black text-gray-700">{filtered.length}</span> leads
          </p>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setPage(1)} disabled={safePage===1}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-extrabold text-gray-600 hover:border-yellow-400 hover:bg-yellow-50 disabled:opacity-30 disabled:cursor-not-allowed transition">«</button>
            <button type="button" onClick={() => setPage(p => Math.max(1, p-1))} disabled={safePage===1}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-extrabold text-gray-600 hover:border-yellow-400 hover:bg-yellow-50 disabled:opacity-30 disabled:cursor-not-allowed transition">‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i+1)
              .filter(n => n===1 || n===totalPages || Math.abs(n-safePage)<=1)
              .reduce((acc, n, idx, arr) => {
                if (idx>0 && n-arr[idx-1]>1) acc.push('…');
                acc.push(n); return acc;
              }, [])
              .map((n, idx) => n === '…'
                ? <span key={`e${idx}`} className="px-1 text-xs text-gray-300">…</span>
                : <button key={n} type="button" onClick={() => setPage(n)}
                    className={`rounded-xl border px-3 py-1.5 text-[11px] font-extrabold transition ${
                      n === safePage
                        ? 'border-black bg-black text-yellow-300 shadow-[0_4px_12px_rgba(0,0,0,0.2)]'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-yellow-400 hover:bg-yellow-50'
                    }`}>{n}</button>
              )
            }
            <button type="button" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={safePage===totalPages}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-extrabold text-gray-600 hover:border-yellow-400 hover:bg-yellow-50 disabled:opacity-30 disabled:cursor-not-allowed transition">Next ›</button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={safePage===totalPages}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-extrabold text-gray-600 hover:border-yellow-400 hover:bg-yellow-50 disabled:opacity-30 disabled:cursor-not-allowed transition">»</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────── Company Leads Tab ───────────────────────────────────────────────────
const CompanyLeadsTab = ({ API_URL }) => {
  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState({ ...EMPTY_COMPANY_FORM });
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState('');
  const [delId, setDelId]       = useState(null);
  const [search, setSearch]     = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/agent/company-leads`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setLeads(r.data || []);
    } catch { setLeads([]); } finally { setLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetch(); }, [fetch]);

  const openNew  = () => { setEditId(null); setForm({ ...EMPTY_COMPANY_FORM, date_of_lead: getTodayDate() }); setFeedback(''); setShowForm(true); };
  const openEdit = (l) => {
    setEditId(l.id);
    setForm({ date_of_lead: l.date_of_lead||getTodayDate(), company_name: l.company_name||'', place: l.place||'',
              district: l.district||'', contact_number: l.contact_number||'', whatsapp_number: l.whatsapp_number||'',
              email_id: l.email_id||'', leads_through: l.leads_through||'', notes: l.notes||'' });
    setFeedback(''); setShowForm(true);
  };
  const onChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setFeedback('');
    try {
      if (editId) {
        const r = await axios.put(`${API_URL}/api/agent/company-leads/${editId}`, form, { headers: { Authorization: `Bearer ${getToken()}` } });
        setLeads(p => p.map(l => l.id===editId ? r.data.lead : l));
      } else {
        const r = await axios.post(`${API_URL}/api/agent/company-leads`, form, { headers: { Authorization: `Bearer ${getToken()}` } });
        setLeads(p => [r.data.lead, ...p]);
      }
      setShowForm(false); setEditId(null); setForm({ ...EMPTY_COMPANY_FORM, date_of_lead: getTodayDate() });
    } catch (err) { setFeedback(err?.response?.data?.error||'Failed to save lead.'); } finally { setSaving(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete this company lead?')) return;
    setDelId(id);
    try {
      await axios.delete(`${API_URL}/api/agent/company-leads/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setLeads(p => p.filter(l => l.id!==id));
    } catch { alert('Failed to delete.'); } finally { setDelId(null); }
  };

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    return !q || l.company_name?.toLowerCase().includes(q) || l.contact_number?.includes(q) || l.district?.toLowerCase().includes(q) || l.leads_through?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex-1">
          <input type="text" placeholder="Search by company, contact, district or source…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 pl-10 text-sm shadow-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100" />
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
        </div>
        <button type="button" onClick={openNew}
          className="shrink-0 rounded-[20px] border border-black bg-black px-5 py-3 text-sm font-extrabold text-yellow-300 shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:bg-[#222] hover:shadow-[0_12px_32px_rgba(0,0,0,0.35)] active:scale-95 transition-all">
          + Add Company Lead
        </button>
      </div>
      {showForm && (
        <div className="mb-7 overflow-hidden rounded-[28px] border border-gray-200 bg-gray-50 shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
          <div className="border-b border-black/10 bg-[linear-gradient(135deg,#0a0a0a,#1f1f1f)] px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.26em] text-yellow-400">{editId ? 'Editing Lead' : 'New Company Lead'}</p>
                <p className="mt-0.5 text-lg font-black text-white">{editId ? 'Update company lead details' : 'Add a new company lead'}</p>
              </div>
              <button type="button" onClick={() => setShowForm(false)}
                className="rounded-2xl border border-white/20 px-4 py-1.5 text-xs font-extrabold text-white/80 hover:bg-white/10 transition">✕ Cancel</button>
            </div>
          </div>
          <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3">
            <div><Lbl req>Date of Lead</Lbl><input type="date" name="date_of_lead" value={form.date_of_lead} onChange={onChange} required className={inputCls} /></div>
            <div><Lbl req>Company Name</Lbl><input type="text" name="company_name" value={form.company_name} onChange={onChange} required placeholder="Company name" className={inputCls} /></div>
            <div><Lbl>Place</Lbl><input type="text" name="place" value={form.place} onChange={onChange} placeholder="Place (optional)" className={inputCls} /></div>
            <div><Lbl>District</Lbl>
              <select name="district" value={form.district} onChange={onChange} className={selectCls}>
                <option value="">Select district</option>
                {KERALA_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><Lbl req>Contact Number</Lbl><input type="tel" name="contact_number" value={form.contact_number} onChange={onChange} required placeholder="Contact number" maxLength={15} className={inputCls} /></div>
            <div><Lbl>WhatsApp Number</Lbl><input type="tel" name="whatsapp_number" value={form.whatsapp_number} onChange={onChange} placeholder="WhatsApp (optional)" maxLength={15} className={inputCls} /></div>
            <div><Lbl>Email ID</Lbl><input type="email" name="email_id" value={form.email_id} onChange={onChange} placeholder="email@example.com" className={inputCls} /></div>
            <div><Lbl req>Leads Through</Lbl>
              <select name="leads_through" value={form.leads_through} onChange={onChange} required className={selectCls}>
                <option value="">Select source</option>
                {LEADS_THROUGH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div><Lbl>Notes</Lbl><textarea name="notes" value={form.notes} onChange={onChange} rows={2} placeholder="Additional notes (optional)" className={`${inputCls} resize-none`} /></div>
            <div className="flex items-end sm:col-span-2 lg:col-span-3 gap-3">
              {feedback && <p className="text-xs font-bold text-rose-600">{feedback}</p>}
              <button type="submit" disabled={saving}
                className="ml-auto rounded-[18px] border border-black bg-black px-7 py-2.5 text-sm font-extrabold text-yellow-300 shadow-[0_6px_20px_rgba(0,0,0,0.25)] hover:bg-[#222] hover:shadow-[0_10px_28px_rgba(0,0,0,0.35)] disabled:opacity-50 transition-all">
                {saving ? 'Saving…' : editId ? 'Update Lead' : 'Save Lead'}
              </button>
            </div>
          </form>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-sm font-semibold text-gray-400">
          <span className="mr-2 animate-spin text-xl">⟳</span> Loading leads…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-[24px] border border-dashed border-gray-300 bg-gray-50/60 py-20 text-center">
          <p className="text-4xl">🏢</p>
          <p className="mt-3 text-base font-black text-gray-500">{search ? 'No leads match your search.' : 'No company leads yet.'}</p>
          <p className="mt-1 text-sm text-gray-400">Click "+ Add Company Lead" to capture your first lead.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[24px] border border-gray-200 bg-white shadow-[0_8px_32px_rgba(15,23,42,0.06)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#111] text-left">
                {['Date','Company','Place','District','Contact','WhatsApp','Email','Source','Notes','Actions'].map(h => (
                  <th key={h} className="whitespace-nowrap px-4 py-3.5 text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr key={l.id} className={`border-b transition-colors hover:bg-yellow-50/50 ${i%2===0?'bg-white':'bg-gray-50/60'}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-400">{l.date_of_lead||'-'}</td>
                  <td className="px-4 py-3 font-extrabold text-gray-900">{l.company_name}</td>
                  <td className="px-4 py-3 text-gray-500">{l.place||'-'}</td>
                  <td className="px-4 py-3 text-gray-500">{l.district||'-'}</td>
                  <td className="whitespace-nowrap px-4 py-3"><a href={`tel:${l.contact_number}`} className="font-bold text-gray-900 hover:underline">{l.contact_number}</a></td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {l.whatsapp_number ? <a href={`https://wa.me/91${l.whatsapp_number}`} target="_blank" rel="noreferrer" className="font-bold text-emerald-600 hover:underline">{l.whatsapp_number}</a> : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{l.email_id||'-'}</td>
                  <td className="px-4 py-3"><SourceBadge src={l.leads_through} /></td>
                  <td className="max-w-[160px] px-4 py-3 text-xs text-gray-400 truncate">{l.notes||'-'}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => openEdit(l)} className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-[11px] font-extrabold text-gray-700 hover:border-yellow-400 hover:bg-yellow-50 transition">Edit</button>
                      <button type="button" onClick={() => onDelete(l.id)} disabled={delId===l.id} className="rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-[11px] font-extrabold text-rose-500 hover:border-rose-400 hover:bg-rose-50 disabled:opacity-50 transition">
                        {delId===l.id?'…':'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────── Agent Dashboard shell ───────────────────────────────────────────────
const AgentDashboard = ({ name, API_URL: propApiUrl }) => {
  const API_URL = propApiUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [tab, setTab]                   = useState('members');
  const [memberLeads, setMemberLeads]   = useState([]);
  const [companyCount, setCompanyCount] = useState(0);

  const handleMemberStats = useCallback((leads) => { setMemberLeads(leads); }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/agent/company-leads`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => setCompanyCount((r.data||[]).length)).catch(() => {});
  }, [API_URL]);

  const totalConversions = memberLeads.filter(l => l.telecaller_status === 'Converted').length;
  const totalRevenue     = totalConversions * CONVERSION_REWARD;
  const today            = getTodayDate();
  const todayLeads       = memberLeads.filter(l => l.date_of_lead === today).length;
  const todayConversions = memberLeads.filter(l => l.telecaller_status === 'Converted' && l.updated_at?.slice(0,10) === today).length;

  const TABS = [
    { key: 'members',   icon: '👤', label: 'Member Leads',  sub: `${memberLeads.length} records` },
    { key: 'companies', icon: '🏢', label: 'Company Leads', sub: `${companyCount} records` },
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_right,rgba(250,204,21,0.10),transparent_22%),linear-gradient(180deg,#f9fafb_0%,#f3f4f6_100%)] px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      <div className="mx-auto w-full max-w-[1920px] overflow-hidden rounded-[32px] border border-gray-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.08)]">

        {/* Header */}
        <div className="bg-[radial-gradient(circle_at_top_right,rgba(250,204,21,0.18),transparent_30%),linear-gradient(135deg,#0a0a0a_0%,#1a1a1a_60%,#111_100%)] px-5 py-6 text-white md:px-8 md:py-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.28em] text-yellow-400">Agent Dashboard</p>
              <h2 className="mt-1.5 text-2xl font-black">{name || 'Agent'}</h2>
              <p className="mt-1 text-sm text-white/55">Capture leads · Forward to telecaller · Track conversions</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur-sm">
              <span className="text-xl">⚡</span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-yellow-400">Role</p>
                <p className="text-sm font-extrabold text-white">Agent</p>
              </div>
            </div>
          </div>

          {/* Stat tiles */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {[
              { label: 'Member Leads',      value: memberLeads.length },
              { label: 'Company Leads',     value: companyCount        },
              { label: 'Forwarded',         value: memberLeads.filter(l=>l.telecaller_name).length },
              { label: 'Converted',         value: totalConversions    },
              { label: 'Revenue Earned',    value: `₹${totalRevenue}`, highlight: true },
              { label: 'Today\'s Leads',    value: todayLeads          },
            ].map(({ label, value, highlight }) => (
              <div key={label} className={`rounded-2xl border border-white/10 px-4 py-3.5 backdrop-blur-sm ${highlight ? 'bg-yellow-400/20 border-yellow-400/30' : 'bg-white/10'}`}>
                <p className={`text-[11px] font-extrabold uppercase tracking-[0.2em] ${highlight ? 'text-yellow-300' : 'text-yellow-300'}`}>{label}</p>
                <p className={`mt-1.5 font-black text-white ${highlight ? 'text-xl' : 'text-2xl'}`}>{value}</p>
                {highlight && <p className="text-[9px] font-bold text-yellow-200/70 mt-0.5">@ ₹{CONVERSION_REWARD}/conversion</p>}
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 py-5 md:px-6">
          <TargetWidget API_URL={API_URL} />
          {/* Tabs */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            {/* Tab buttons */}
            <div className="flex flex-wrap gap-2">
              {TABS.map(t => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  className={`rounded-[20px] border px-5 py-2.5 text-left transition ${
                    tab === t.key
                      ? 'border-black bg-black text-white shadow-[0_8px_24px_rgba(0,0,0,0.22)]'
                      : 'border-gray-200 bg-white text-black hover:border-yellow-400'
                  }`}>
                  <p className={`text-sm font-extrabold ${tab === t.key ? 'text-yellow-300' : 'text-gray-700'}`}>{t.icon} {t.label}</p>
                  <p className={`text-[10px] mt-0.5 ${tab === t.key ? 'text-white/55' : 'text-gray-400'}`}>{t.sub}</p>
                </button>
              ))}
            </div>

            {/* Today's progress — inline with tabs */}
            <div className="ml-auto flex flex-wrap items-center gap-4 rounded-[20px] border border-gray-100 bg-[linear-gradient(135deg,#fafafa,#f5f5f5)] px-4 py-2.5 shadow-sm">
              <p className="hidden sm:block text-[9px] font-black uppercase tracking-[0.24em] text-gray-300 whitespace-nowrap">{new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</p>
              <div className="flex items-center gap-2 min-w-[130px]">
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-gray-400 whitespace-nowrap">Leads</p>
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden w-20">
                  <div className={`h-full rounded-full transition-all duration-500 ${todayLeads >= DAILY_LEAD_TARGET ? 'bg-emerald-500' : 'bg-yellow-400'}`} style={{ width: `${Math.min(100, Math.round((todayLeads/DAILY_LEAD_TARGET)*100))}%` }} />
                </div>
                <p className={`text-[10px] font-black whitespace-nowrap ${todayLeads >= DAILY_LEAD_TARGET ? 'text-emerald-600' : 'text-gray-700'}`}>{todayLeads}<span className="font-normal text-gray-400">/{DAILY_LEAD_TARGET}</span></p>
              </div>
              <div className="flex items-center gap-2 min-w-[130px]">
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-gray-400 whitespace-nowrap">Conv.</p>
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden w-20">
                  <div className={`h-full rounded-full transition-all duration-500 ${todayConversions >= DAILY_CONVERSION_TARGET ? 'bg-emerald-500' : 'bg-black'}`} style={{ width: `${Math.min(100, Math.round((todayConversions/DAILY_CONVERSION_TARGET)*100))}%` }} />
                </div>
                <p className={`text-[10px] font-black whitespace-nowrap ${todayConversions >= DAILY_CONVERSION_TARGET ? 'text-emerald-600' : 'text-gray-700'}`}>{todayConversions}<span className="font-normal text-gray-400">/{DAILY_CONVERSION_TARGET}</span></p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-[0_4px_24px_rgba(15,23,42,0.04)]">
            {tab === 'members'
              ? <MemberLeadsTab API_URL={API_URL} onStatsChange={handleMemberStats} />
              : <CompanyLeadsTab API_URL={API_URL} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
